import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { GraduationCap, Users, HeartHandshake, ArrowRight, Check, Calendar } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { useCalendly } from "@/components/CalendlyButton";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const offerings = [
  {
    icon: GraduationCap,
    title: "Private Leadership & Career Strategy",
    desc: "A private 90-day coaching experience for accomplished mothers navigating a meaningful career or leadership inflection point — designed to clarify what you want next, strengthen how you position and advocate for the value you create, and build an outcome-led strategy for moving forward.",
    interest: "Private Leadership & Career Strategy",
    bookable: true,
  },
  {
    icon: Users,
    title: "Leadership Workshops",
    desc: "Interactive learning experiences designed to help ambitious women strengthen their leadership, establish sustainable systems, and connect with a community of like-minded professionals.",
    interest: "Leadership Workshops",
  },
  {
    icon: HeartHandshake,
    title: "Community",
    desc: "A thoughtfully designed space where ambitious mothers learn, grow, and lead together through mentorship, shared experiences, and meaningful connection.",
    aside: "Because leadership was never meant to be carried alone.",
    interest: "Community",
  },
];

const interestOptions = [
  "Private Leadership & Career Strategy",
  "Leadership Workshops",
  "Community",
  "Not sure yet — tell me more",
];

const inquiryBenefits = [
  "A personal response from Jenna",
  "Guidance on the right coaching or workshop fit",
  "Practical leadership resources",
  "Community and workshop invitations",
  "A direct line to The Thrive & Mother Company",
];

const scrollToForm = () => {
  const el = document.getElementById("priority-list");
  if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
};

export const WorkWithJenna = () => {
  const [form, setForm] = useState({ name: "", email: "", interest: "", message: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const openCalendly = useCalendly();

  const selectInterestAndScroll = (interest) => {
    setForm((f) => ({ ...f, interest }));
    scrollToForm();
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.interest) {
      toast.error("Please choose what you're interested in.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/inquiry`, form);
      setDone(true);
      toast.success("Thank you. Jenna will personally be in touch.");
      setForm({ name: "", email: "", interest: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="work-with-jenna" className="py-24 md:py-40" data-testid="work-with-jenna">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* HEADER */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end mb-16">
          <div className="lg:col-span-7">
            <Reveal>
              <p className="text-xs uppercase tracking-[0.3em] text-terracotta mb-6">Lead with Jenna</p>
              <h2 className="font-serif font-light text-wine text-4xl md:text-5xl leading-tight">
                Leadership transformation for every stage of your journey.
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={0.1}>
              <p className="text-charcoal/75 leading-relaxed">
                Whether you&rsquo;re beginning with a five-minute weekly reset or seeking personalized executive
                coaching, every Thrive &amp; Mother offering is designed to help you lead with greater clarity,
                confidence, and purpose.
              </p>
              <p className="mt-4 text-terracotta font-medium">
                Choose the level of support that&rsquo;s right for you.
              </p>
            </Reveal>
          </div>
        </div>

        {/* OFFERING CARDS */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-gold/30 border border-gold/30 mb-24">
          {offerings.map((o, i) => {
            const Icon = o.icon;
            return (
              <Reveal key={o.title} delay={i * 0.08}>
                <div
                  className="bg-cream p-10 md:p-12 h-full flex flex-col"
                  data-testid={`offering-${o.title.toLowerCase().replace(/\s/g, "-")}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="w-12 h-12 rounded-full border border-terracotta/40 flex items-center justify-center text-terracotta">
                      <Icon size={20} strokeWidth={1.5} />
                    </span>
                  </div>
                  <h3 className="font-serif text-2xl md:text-3xl text-wine mt-8">{o.title}</h3>
                  <p className="mt-4 text-charcoal/75 leading-relaxed">{o.desc}</p>
                  {o.aside && (
                    <p className="mt-4 font-serif italic text-terracotta leading-relaxed">{o.aside}</p>
                  )}
                  <button
                    onClick={() => o.bookable ? openCalendly({ medium: "offering-card", content: "private-coaching" }) : selectInterestAndScroll(o.interest)}
                    data-testid={`offering-cta-${o.title.toLowerCase().replace(/\s/g, "-")}`}
                    className="group mt-auto pt-8 inline-flex items-center gap-2 text-terracotta font-medium text-sm hover:text-wine transition-colors self-start"
                  >
                    {o.bookable && <Calendar size={14} />}
                    {o.bookable ? "Book a Discovery Call" : "Send Inquiry"}
                    <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                  </button>
                </div>
              </Reveal>
            );
          })}
        </div>

        {/* INQUIRY FORM */}
        <div
          id="priority-list"
          className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start scroll-mt-32"
        >
          <div className="lg:col-span-5">
            <Reveal>
              <p className="text-xs uppercase tracking-[0.3em] text-terracotta mb-6">Get in Touch</p>
              <h3 className="font-serif font-light text-wine text-3xl md:text-4xl leading-tight">
                When you reach out, expect:
              </h3>
            </Reveal>
            <Reveal delay={0.1}>
              <ul className="mt-8 space-y-3">
                {inquiryBenefits.map((b) => (
                  <li key={b} className="flex items-start gap-3 text-charcoal/85">
                    <Check size={16} className="mt-1 text-terracotta flex-none" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            {done ? (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7 }}
                data-testid="inquiry-success"
                className="border border-gold/40 p-10 md:p-12 bg-blush/20"
              >
                <h3 className="font-serif text-3xl text-wine">Thank you.</h3>
                <p className="mt-4 text-charcoal/75 leading-relaxed">
                  Jenna will personally reach out with next steps for the offering you&rsquo;re interested in.
                </p>
                <button
                  onClick={() => setDone(false)}
                  data-testid="inquiry-reset"
                  className="link-underline inline-flex items-center gap-2 mt-6 text-terracotta font-medium"
                >
                  Send another inquiry <ArrowRight size={16} />
                </button>
              </motion.div>
            ) : (
              <Reveal delay={0.1}>
                <form onSubmit={submit} className="space-y-8" data-testid="inquiry-form">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                    <div>
                      <label className="text-xs uppercase tracking-[0.2em] text-charcoal/60">Name</label>
                      <input
                        required
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        data-testid="inquiry-name-input"
                        className="w-full bg-transparent border-b-2 border-gold/50 py-3 mt-2 text-charcoal focus:outline-none focus:border-terracotta transition-colors"
                      />
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-[0.2em] text-charcoal/60">Email</label>
                      <input
                        required
                        type="email"
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        data-testid="inquiry-email-input"
                        className="w-full bg-transparent border-b-2 border-gold/50 py-3 mt-2 text-charcoal focus:outline-none focus:border-terracotta transition-colors"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-xs uppercase tracking-[0.2em] text-charcoal/60">
                      I&rsquo;m interested in
                    </label>
                    <Select value={form.interest} onValueChange={(v) => setForm({ ...form, interest: v })}>
                      <SelectTrigger
                        data-testid="inquiry-interest-trigger"
                        className="w-full bg-transparent border-0 border-b-2 border-gold/50 rounded-none px-0 py-3 mt-2 h-auto text-base text-charcoal shadow-none focus:ring-0 focus:border-terracotta data-[placeholder]:text-charcoal/40"
                      >
                        <SelectValue placeholder="Select an offering" />
                      </SelectTrigger>
                      <SelectContent className="bg-cream border-gold/40">
                        {interestOptions.map((opt) => (
                          <SelectItem
                            key={opt}
                            value={opt}
                            data-testid={`interest-${opt.toLowerCase().replace(/\s/g, "-")}`}
                            className="focus:bg-blush/40 focus:text-wine"
                          >
                            {opt}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <label className="text-xs uppercase tracking-[0.2em] text-charcoal/60">
                      Anything you&rsquo;d like to share (optional)
                    </label>
                    <textarea
                      rows={3}
                      value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                      data-testid="inquiry-message-input"
                      className="w-full bg-transparent border-b-2 border-gold/50 py-3 mt-2 text-charcoal focus:outline-none focus:border-terracotta transition-colors resize-none"
                    />
                  </div>

                  <motion.button
                    whileTap={{ scale: 0.97 }}
                    disabled={sending}
                    type="submit"
                    data-testid="inquiry-submit-button"
                    className="group inline-flex items-center gap-3 rounded-full bg-terracotta text-cream px-10 py-4 text-sm font-medium tracking-wide hover:bg-wine transition-colors duration-300 disabled:opacity-60"
                  >
                    {sending ? "Sending…" : "Send Inquiry"}
                    <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                  </motion.button>
                </form>
              </Reveal>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
